import React, { useContext, useEffect, useMemo, useState } from 'react';
import { PropagateLoader } from 'react-spinners';
import { SubscriptionPlan } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import AddToCart from '@components/shared/AddToCart';
import Colors from '@components/shared/Colors';
import ShoppingAddons from '@components/shared/design/ShoppingAddons';
import { Typography, TextVariant } from '@components/shared/design/Typography';
import DiscountedPriceDisplay from '@components/shared/DiscountedPriceDisplay';
import { TypeOfPurchase } from '@components/shared/PurchaseOptions';
import QuantityAddToCart from '@components/shared/QuantityAddToCart';
import ReplacedText from '@components/shared/ReplacedText';
import SimpleBox from '@components/shared/SimpleBox';
import { Superscript } from '@components/shared/TextTypes';
import { generateProductBuyOptions } from '@components/shared/utilProductOptions';
import { CartItemType, ProductConfiguration, ProductCode, ProductBuyOption, Prices, PromotionObject } from '@contracts';
import SelectedRadio from '@images/selected-radio.svg';
import UnselectedRadio from '@images/unselected-radio.svg';
import { createCartItem, getCartItemType } from '@utils/cart';
import { isPromotionActive } from '@utils/contextHelpers';
import { getSubscriptionPlans } from '@utils/getSubscriptionPlans';
import { getSanitizedKey } from '@utils/helpers';
import { getFromLocalStorage } from '@utils/localStorage';
import { getCoinsFromPrice, getDiscountedProductPrice, getNewProductPrice, getPoundsFromPrice } from '@utils/price';
import { getDiscountPercent } from '@utils/subscriptions';
import ConsultationPriceDetails from './ConsultationPriceDetails';
import ProductBuyOptionsInfo from './ProductBuyOptionsInfo';
import ProductItemIcon from './ProductItemIcon';
import ProductPromotion from './ProductPromotion';
import { useShoppingContext } from './ProductShoppingContext';
import ProductWhatsInside from './ProductWhatsInside';

export const CONSULTATION_DURATION_TEXT = '30 min';

const ProductBuyOptions: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency, country } = state;
  const {
    productId,
    productOptionsInfo,
    productHeaderData,
    isUsingNewDesignSystem,
    selectedAddonCartItems,
    selectedOptionCartItem,
    setSelectedOptionCartItem
  } = useShoppingContext();
  const { productCode, configurationTitle, paymentOptionsTitle, addonOptionsTitle } = productHeaderData;
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[] | null>(null);
  const [selectedOption, setSelectedOption] = useState<ProductBuyOption | null>();
  const [selectedProductId, setSelectedProductId] = useState<string | null>();
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>();
  const [quantity, setQuantity] = useState(1);
  const [buyPrice, setBuyPrice] = useState(-1);
  const showQuantitySelector = !selectedPlanCode && !isUsingNewDesignSystem;
  const checkoutButton = false;
  const configurations: ProductConfiguration[] = useMemo(() => (getFromLocalStorage('configurations', true) as any) ?? [], []);
  const productOptions = useMemo(() => generateProductBuyOptions(productOptionsInfo, configurations), [productOptionsInfo, configurations]);
  const hasMultipleOptions = productOptions.length > 1;
  const hasConfiguration = hasMultipleOptions && configurationTitle !== '';
  const [prices, setPrices] = useState<Prices>({});
  const hasSubscriptionSku = Boolean(selectedOption?.subscriptionSku) && prices[selectedOption?.subscriptionSku] >= 0;
  const promotions: PromotionObject[] = getFromLocalStorage('promotions', true) ?? [];
  const addonProducts = productHeaderData.addonProducts?.split(',') as ProductCode[];

  const hasActivePromotionForAddon = useMemo(() => {
    if (!addonProducts?.length) return false;
    return promotions.some((promo) => isPromotionActive(promo, country) && addonProducts.includes(promo.freeGift as ProductCode));
  }, [promotions, addonProducts, country]);

  useEffect(() => {
    const fetchPrices = async () => {
      const updates = await Promise.all(
        productOptions.map(async (option) => {
          const oneOffPrice = await getNewProductPrice(currency, country, option?.oneOffSku, undefined);
          const subscriptionPrice = await getNewProductPrice(currency, country, option?.subscriptionSku, option?.subscriptionPlan);
          return {
            [option?.oneOffSku]: oneOffPrice,
            [option?.subscriptionSku]: subscriptionPrice
          };
        })
      );

      // Flatten the results into the priceUpdate object.
      const priceUpdate = Object.assign({}, ...updates);
      setPrices(priceUpdate);
    };

    fetchPrices();
  }, [currency, country, productOptions]);

  useEffect(() => {
    setSelectedOption(getPreselectedOption() ?? productOptions[0]);

    (async () => {
      await getPlans();
    })();
  }, []);

  const getPlans = async () => {
    const plans = await getSubscriptionPlans(productCode);
    setSubscriptionPlans(plans);
  };

  useEffect(() => {
    if (selectedOption?.subscriptionPlan === selectedPlanCode) {
      setSelectedProductId(selectedOption?.subscriptionSku ?? selectedOption?.oneOffSku);
    } else {
      setSelectedPlanCode(null);
      setSelectedProductId(selectedOption?.oneOffSku ?? selectedOption?.subscriptionSku);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedProductId) {
      setBuyPrice(prices[selectedProductId]);
    }
  }, [selectedProductId, selectedPlanCode, prices]);

  useEffect(() => {
    if (!subscriptionPlans) return;

    const newCartItem = generateCartItem();
    setSelectedOptionCartItem(newCartItem);
  }, [buyPrice, quantity, selectedProductId, subscriptionPlans]);

  const getPreselectedOption = () => {
    const preselected = productOptions.find((option) => option.oneOffSku === ProductCode.VMSAdvanced);

    return preselected;
  };

  const renderPrice = (value: number, notDiscountedPrice?: number) => {
    const poundsPrice = getPoundsFromPrice(value);
    const coinsPrice = getCoinsFromPrice(value);
    return (
      <Price $color="transparent" className={productCode} $discounted={!!notDiscountedPrice}>
        <div className={`price-text ${notDiscountedPrice ? 'pinktext' : ''}`}>
          {notDiscountedPrice && (
            <NotDiscountedPrice>
              {currency.symbol}
              {notDiscountedPrice}
            </NotDiscountedPrice>
          )}
          {currency.symbol}
          <div>{poundsPrice}</div>
          {coinsPrice !== '00' ? <Superscript style={{ marginLeft: 2 }}>{getCoinsFromPrice(value)}</Superscript> : null}
        </div>
      </Price>
    );
  };

  const generateCartItem = () => {
    const itemProductCode = (selectedProductId || productId) as ProductCode;
    if (!itemProductCode) {
      console.error('No product code available for creating cart item.');
      return null;
    }
    const optionType = selectedPlanCode ? CartItemType.Subscription : getCartItemType(itemProductCode);
    const cartItemPrice = Number(buyPrice?.toFixed(2));
    const cartItemEditable = true;
    const selectedPlan = subscriptionPlans.find((plan) => plan.planCode === selectedPlanCode);
    const createdCartItem = createCartItem(
      itemProductCode,
      quantity,
      cartItemPrice,
      cartItemEditable,
      optionType,
      undefined,
      undefined,
      [],
      selectedPlan
    );

    return createdCartItem;
  };

  const renderPurchaseOptions = () => {
    const hasOneOffSku = Boolean(selectedOption?.oneOffSku);
    if (hasOneOffSku && !hasSubscriptionSku && getCartItemType(selectedOption?.oneOffSku) === CartItemType.Consultation) {
      const consultationDuration = CONSULTATION_DURATION_TEXT;
      return (
        <ConsultationDetails data-testid="consultation-details-container">
          <ConsultationPriceDetails currencySymbol={currency.symbol} price={buyPrice} duration={consultationDuration} />
        </ConsultationDetails>
      );
    }

    if (hasMultipleOptions && !hasConfiguration) {
      return (
        <PurchaseOptionsWrap>
          {productOptions.map((option) => (
            <PurchaseOptionsMapWrap key={`purchase-option-${option.title}`}>
              {hasSubscriptionSku && renderOption(option, TypeOfPurchase.SubscriptionPurchase)}
              {hasOneOffSku && renderOption(option, TypeOfPurchase.OneOffPurchase)}
            </PurchaseOptionsMapWrap>
          ))}
        </PurchaseOptionsWrap>
      );
    }

    if (!selectedOption || !(hasSubscriptionSku || hasOneOffSku)) return null;

    return (
      <PurchaseOptionsWrap>
        {hasSubscriptionSku && renderOption(selectedOption, TypeOfPurchase.SubscriptionPurchase)}
        {hasOneOffSku && renderOption(selectedOption, TypeOfPurchase.OneOffPurchase)}
      </PurchaseOptionsWrap>
    );
  };

  const renderOption = (option: ProductBuyOption, optionType: TypeOfPurchase) => {
    const { title, includes, optionInfo, oneOffSku, subscriptionSku, oneOffCount, subscriptionCount, subscriptionPlan } = option;
    const oneOff = optionType === TypeOfPurchase.OneOffPurchase;
    const showDiscount = !oneOff;
    const showOptions = !subscriptionSku || !oneOff;
    const optionProductId = oneOff ? oneOffSku : subscriptionSku;
    const itemCount = oneOff ? oneOffCount : subscriptionCount;
    const isSelected = selectedProductId === optionProductId;
    const backupTitle = !oneOff ? '3-month subscription' : 'Buy one-off';
    const titleText = title !== '' ? title : backupTitle;
    const price = oneOff ? prices[oneOffSku ?? ''] : prices[subscriptionSku ?? ''];
    const foundPlan = subscriptionPlans?.find((plan) => plan.planCode === subscriptionPlan);
    const discountPercent = foundPlan ? getDiscountPercent(foundPlan, country) : 0;

    const notDiscountedPrice: number | undefined = getDiscountedProductPrice(oneOffSku, country);
    const badgeText: string | undefined = notDiscountedPrice
      ? `Save ${Math.round(((notDiscountedPrice - price) / notDiscountedPrice) * 100)}%`
      : undefined;

    const setSelected = (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;

      /*
       * Used not to trigger sellection if the user clicks on the info toggle
       * Because the info toggle should not trigger selection
       */
      if (target.closest('#product-buy-options-info-toggle')) {
        return;
      }
      setSelectedPlanCode(optionType === TypeOfPurchase.SubscriptionPurchase ? subscriptionPlan : null);
      setSelectedProductId(optionProductId);
    };

    const renderAdditionalInfo = () => {
      if (optionInfo && optionInfo.length > 0) {
        return <ProductBuyOptionsInfo info={optionInfo} openDefault={isSelected} />;
      }

      if (includes && showOptions) {
        return (
          <OptionContains>
            {includes?.map((item, index) => {
              if (item === '') return null;
              const sanitizedKey = item ? getSanitizedKey(item) : index;
              return <li key={sanitizedKey}>{item}</li>;
            })}
          </OptionContains>
        );
      }

      return null;
    };

    return (
      <OptionWrap>
        <SimpleBox onClick={(e) => (e ? setSelected(e) : null)} selected={isSelected} badgeText={badgeText} selectable isRadioSelection>
          <OptionWrapInner>
            {showDiscount ? <DiscountBox>{t('components.subscription-options.save', { percent: discountPercent })}</DiscountBox> : null}
            <input hidden type="radio" value={optionProductId} />
            <RadioIndicator>{isSelected ? <SelectedRadio /> : <UnselectedRadio />}</RadioIndicator>
            <PurchaseOptionTextWrap>
              <OptionText>
                <ReplacedText as={OptionTitle} $textColor={Colors.navyBlue}>
                  {titleText}
                </ReplacedText>
              </OptionText>
              <OptionAux>
                {itemCount > 0 ? (
                  <ItemCount>
                    <span>{itemCount}</span>
                    <ProductItemIcon code={productId} />
                  </ItemCount>
                ) : null}
                {price >= 0 ? <DiscountedPriceDisplay originalPrice={notDiscountedPrice} price={price} /> : null}
              </OptionAux>
            </PurchaseOptionTextWrap>
          </OptionWrapInner>
          {renderAdditionalInfo()}
        </SimpleBox>
      </OptionWrap>
    );
  };

  const variants = selectedPlanCode ? selectedOption?.variants[selectedPlanCode] : selectedOption?.variants['one-off'];

  const getSubmitButtonText = () => {
    if (checkoutButton) {
      return t('components.checkout-now');
    }
    if (selectedOptionCartItem?.type === CartItemType.Consultation) {
      return t('components.book-a-consultation');
    }
    return '';
  };

  if (!subscriptionPlans) {
    return (
      <LoadingWrap>
        <PropagateLoader color={Colors.navyBlue} />
      </LoadingWrap>
    );
  }

  return (
    <Container $textColor={Colors.navyBlue}>
      {hasConfiguration && (
        <ConfigureSection>
          <Typography $variant={TextVariant.UpperText5}>{configurationTitle}</Typography>
          <ProductOptions>
            {productOptions.map((option, index) => {
              let active = false;
              if (option.oneOffSku === selectedOption?.oneOffSku || option.subscriptionSku === selectedOption?.subscriptionSku) {
                active = true;
              }
              return (
                <ProductOptionButton key={index} $active={active} onClick={() => setSelectedOption(option)}>
                  {option.icon}
                  <span>{option.label}</span>
                </ProductOptionButton>
              );
            })}
          </ProductOptions>
        </ConfigureSection>
      )}
      <BuyOptionSection>
        {paymentOptionsTitle && (hasSubscriptionSku || isUsingNewDesignSystem) ? (
          <Typography $variant={TextVariant.UpperText5}>{t(paymentOptionsTitle)}</Typography>
        ) : null}
        {renderPurchaseOptions()}
      </BuyOptionSection>
      {addonOptionsTitle && !hasActivePromotionForAddon ? (
        <BuyOptionSection>
          {addonOptionsTitle ? <Typography $variant={TextVariant.UpperText5}>{t(addonOptionsTitle)}</Typography> : null}
          <ShoppingAddons />
        </BuyOptionSection>
      ) : null}
      <ProductPromotion productId={productId} />
      <CartWrap>
        {showQuantitySelector ? (
          <QuantityWrap>
            <QuantityAddToCart quantity={quantity} onAdd={() => setQuantity(quantity + 1)} onRemove={() => setQuantity(quantity - 1)} />
            <QuantityPrice>{buyPrice >= 0 ? renderPrice(quantity * buyPrice) : ''}</QuantityPrice>
          </QuantityWrap>
        ) : null}
        {selectedOptionCartItem ? (
          <AddToCartStyled
            cartItem={selectedOptionCartItem}
            addonCartItems={selectedAddonCartItems}
            checkoutRedirect={checkoutButton}
            buttonText={getSubmitButtonText()}
          />
        ) : null}
      </CartWrap>
      {variants && (
        <ConfigureSection>
          <Typography $variant={TextVariant.UpperText5}>{t('components.whats-inside')}</Typography>
          <ProductWhatsInside variants={variants} />
        </ConfigureSection>
      )}
    </Container>
  );
};

export default ProductBuyOptions;

const Container = styled.div<{ $textColor?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  color: ${(props) => props.$textColor};
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  min-height: 60px;
  justify-content: center;
  width: 100%;
`;

const ConfigureSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  align-self: stretch;
  width: 100%;
`;

const BuyOptionSection = styled(ConfigureSection)``;

const ProductOptions = styled.div`
  display: flex;
  height: 50px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid ${Colors.paleGold};
`;

const ProductOptionButton = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;

  text-align: center;
  font-family: BrownStd;
  font-size: 16px;
  font-weight: ${(props) => (props.$active ? '700' : '400')};
  line-height: 120%;
  color: ${Colors.navyBlue};

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  border-left: 1px solid ${Colors.paleGold};
  background: ${(props) => (props.$active ? Colors.paleGold : Colors.cream)};
  transition: all 0.2s ease-in-out;

  &:first-child {
    border-left: none;
    border-radius: 12px 0 0 12px;
  }

  &:last-child {
    border-radius: 0 12px 12px 0;
  }

  @media (max-width: 400px) {
    gap: 0;
  }
`;

const PurchaseOptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 15px;
`;

const PurchaseOptionsMapWrap = styled.div`
  max-width: 100%;
`;

const PurchaseOptionTextWrap = styled.div`
  width: 100%;
  padding-left: 8px;
  display: flex;
  justify-content: space-between;
`;

export const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  flex: none;
  flex-grow: 0;
  flex-shrink: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const OptionTitle = styled.p<{ $textColor?: string }>`
  font-family: BrownStd;
  font-size: 16px;
  line-height: 26px;
  font-style: normal;
  font-weight: 700;
  text-align: left;
  color: ${(props) => props.$textColor};

  @media (max-width: 1024px) {
    font-size: 15px;
  }
`;

const OptionContains = styled.ul`
  list-style: none;
  margin: 0;
  text-align: left;
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 4px;
  flex-wrap: wrap;

  &:empty {
    display: none;
  }

  & > li {
    font-family: 'BrownStd';
    font-size: 11px;
    font-weight: 400;
    line-height: 130%;
    color: inherit;
    display: flex;
    padding: 4px 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    background: ${Colors.ecru};
  }
`;

interface IPrice {
  $color: string;
  $discounted?: boolean;
}

const NotDiscountedPrice = styled.p`
  font-size: 18px;
  color: ${Colors.navyBlue};
  opacity: 50%;
  text-decoration: line-through;
  margin-right: 8px;
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 110%;
  color: inherit;
`;

export const OptionSubLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 5px;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;

  @media (max-width: 1024px) {
    font-size: 12px;
    line-height: 150%;
  }
`;

export const Price = styled.div<IPrice>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  height: 100%;
  text-align: center;
  color: inherit;
  background: ${(props) => (props.color !== '' ? props.color : `${Colors.paleGold50}`)};

  &.screening_kit {
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
  }

  & .pinktext {
    color: ${Colors.pink};

    > p {
      color: ${Colors.navyBlue};
    }
  }

  & > div {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: baseline;
    padding: 0;
    flex: none;
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 110%;
    color: inherit;
  }

  & > span {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 100%;
    opacity: 0.5;
    margin-top: 2px;
    color: inherit;
  }
`;

const OptionWrap = styled.div`
  .simple-box-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding: 0 4px;
    max-width: 100%;
    position: relative;
  }
`;

const OptionWrapInner = styled.div`
  display: flex;
  align-items: center;
`;

const RadioIndicator = styled.div`
  width: 20px;
  height: 20px;
`;

const OptionAux = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  margin-left: 10px;
`;

const ItemCount = styled.div`
  display: flex;
  padding: 0 7px 0 0;
  align-items: center;
  gap: 5px;
  border-right: 1px solid rgba(0, 29, 59, 0.1);

  & > span {
    font-family: 'BrownStd';
    font-size: 14px;
    font-weight: 400;
    line-height: 120%;
    color: inherit;
  }

  & > svg {
    height: 18px;
  }
`;

const DiscountBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.lilac};
  color: ${Colors.cream};
  border-radius: 2px;
  padding: 3px 5px;

  font-family: BrownStd;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  position: absolute;
  top: -24px;
  right: 12px;
`;

const AddToCartStyled = styled(AddToCart)`
  background-color: ${Colors.lilac};
  width: 100%;
  height: 60px;

  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 1px;
  text-transform: uppercase;

  @media (max-width: 1024px) {
    height: 50px;
  }
`;

const CartWrap = styled.div`
  width: 100%;
`;

const QuantityWrap = styled.div`
  border-top: 1px solid ${Colors.ecru};
  width: 100%;
  border-radius: 2px 2px 0px 0px;
  border: 1px solid ${Colors.ecru};
  background: ${Colors.cream};
  display: flex;
  flex-direction: row;
  align-items: center;

  & .quantity-container {
    padding: 10px 15px;
  }

  & + button {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const QuantityPrice = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  & .price-text {
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 110%; /* 22px */
  }
`;

const ConsultationDetails = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;
